'use client';
import React, { useState } from 'react';
import { Shield, TrendingUp, PieChart, CheckCircle, AlertTriangle, Info, ChevronRight, ChevronLeft, Lock, BarChart3, Users, Clock, XCircle, Landmark, Zap, Eye, Download, RefreshCw, BadgeCheck, AlertCircle, Building2, Hash, CreditCard, Banknote, Globe, Wallet, FileCheck, ArrowRight } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import MetricCard from '@/components/ui/MetricCard';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
type StepId = 'eligibility' | 'limits' | 'funding' | 'allocation' | 'escrow' | 'confirmation';
type EligibilityStatus = 'checking' | 'passed' | 'failed' | 'warning';
type InvestorTier = 'retail' | 'sophisticated' | 'professional' | 'institutional';
type FundingMethodId = 'bank_wire' | 'ach' | 'swift' | 'crypto';

interface EligibilityCheck {
  id: string;
  label: string;
  description: string;
  status: 'pass' | 'fail' | 'warning' | 'checking';
  detail?: string;
}

interface LimitRule {
  id: string;
  label: string;
  current: number;
  limit: number;
  unit: string;
  status: 'ok' | 'warning' | 'breach';
}

interface AllocationBucket {
  id: string;
  label: string;
  currentPct: number;
  afterPct: number;
  color: string;
}

interface FundingMethod {
  id: FundingMethodId;
  label: string;
  description: string;
  icon: React.ElementType;
  processingTime: string;
  fee: string;
  feeAmount: number; // as fraction of amount
  minAmount: number;
  maxAmount: number;
  details: { label: string; value: string }[];
  badge?: string;
  badgeColor?: string;
}

interface AuditEvent {
  ts: string;
  action: string;
  detail: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS: { id: StepId; label: string; icon: React.ElementType }[] = [
  { id: 'eligibility', label: 'Eligibility', icon: Shield },
  { id: 'limits', label: 'Investor Limits', icon: BarChart3 },
  { id: 'funding', label: 'Funding Method', icon: CreditCard },
  { id: 'allocation', label: 'Portfolio Allocation', icon: PieChart },
  { id: 'escrow', label: 'Escrow Agreement', icon: Landmark },
  { id: 'confirmation', label: 'Confirmation', icon: CheckCircle },
];

const FUNDING_METHODS: FundingMethod[] = [
  {
    id: 'bank_wire',
    label: 'Domestic Bank Wire',
    description: 'Transfer funds directly from your registered bank account via domestic wire transfer.',
    icon: Landmark,
    processingTime: '1–2 business days',
    fee: 'No fee',
    feeAmount: 0,
    minAmount: 10_000,
    maxAmount: 5_000_000,
    badge: 'Recommended',
    badgeColor: 'bg-positive/10 text-positive border-positive/20',
    details: [
      { label: 'Bank Name', value: 'First National Bank' },
      { label: 'Account Name', value: 'CrowdVault Escrow Services LLC' },
      { label: 'Account Number', value: '••••••7842' },
      { label: 'Routing Number', value: '021000021' },
      { label: 'Reference', value: 'FNESC-ESC-2026-0047 / INV-2026-00882' },
    ],
  },
  {
    id: 'ach',
    label: 'ACH Transfer',
    description: 'Automated Clearing House transfer from your linked US bank account. Lower cost, slightly longer settlement.',
    icon: ArrowRight,
    processingTime: '3–5 business days',
    fee: 'No fee',
    feeAmount: 0,
    minAmount: 5_000,
    maxAmount: 500_000,
    details: [
      { label: 'Bank Name', value: 'First National Bank' },
      { label: 'Account Name', value: 'CrowdVault Escrow Services LLC' },
      { label: 'Account Number', value: '••••••7842' },
      { label: 'Routing Number', value: '021000021' },
      { label: 'ACH Type', value: 'CCD (Corporate Credit or Debit)' },
    ],
  },
  {
    id: 'swift',
    label: 'International SWIFT Wire',
    description: 'For international investors. Transfer via SWIFT network from your overseas bank account.',
    icon: Globe,
    processingTime: '2–4 business days',
    fee: '0.15% (min $50)',
    feeAmount: 0.0015,
    minAmount: 25_000,
    maxAmount: 10_000_000,
    details: [
      { label: 'SWIFT / BIC Code', value: 'FNBKUS33XXX' },
      { label: 'Bank Name', value: 'First National Bank, New York' },
      { label: 'Account Name', value: 'CrowdVault Escrow Services LLC' },
      { label: 'IBAN / Account', value: 'US••••••••••••7842' },
      { label: 'Correspondent Bank', value: 'JPMorgan Chase, New York' },
    ],
  },
  {
    id: 'crypto',
    label: 'Digital Asset (USDC / USDT)',
    description: 'Transfer stablecoins from a verified wallet. Subject to additional compliance checks.',
    icon: Wallet,
    processingTime: '30–60 minutes',
    fee: 'Network gas fees apply',
    feeAmount: 0,
    minAmount: 25_000,
    maxAmount: 1_000_000,
    badge: 'Beta',
    badgeColor: 'bg-accent/10 text-accent border-accent/20',
    details: [
      { label: 'Accepted Assets', value: 'USDC, USDT (ERC-20 / Polygon)' },
      { label: 'Wallet Address', value: '0x3f5C...e8A2 (Ethereum Mainnet)' },
      { label: 'Polygon Address', value: '0x3f5C...e8A2 (Polygon)' },
      { label: 'Memo / Reference', value: 'INV-2026-00882-CAMP-0047' },
      { label: 'Compliance Note', value: 'Source-of-funds declaration required' },
    ],
  },
];

const DISCLOSURES = [
  {
    id: 'd1',
    title: 'Investment Risk Disclosure',
    body: 'Private market investments involve significant risk, including the potential loss of the entire invested amount. Past performance is not indicative of future results. This investment is illiquid and may not be redeemable for the full tenor of the fund (7 years).',
    required: true,
  },
  {
    id: 'd2',
    title: 'Regulatory & Suitability Disclosure',
    body: 'This offering is made pursuant to SEC Regulation D, Rule 506(c) and is available only to verified accredited investors. By proceeding, you confirm your accredited investor status and that this investment is suitable for your financial situation and risk tolerance.',
    required: true,
  },
  {
    id: 'd3',
    title: 'Funding Source Declaration',
    body: 'You confirm that the funds used for this investment are from legitimate sources and are not derived from any illegal activity. You consent to source-of-funds verification as required by applicable AML/CFT regulations.',
    required: true,
  },
  {
    id: 'd4',
    title: 'Escrow & Settlement Terms',
    body: 'Funds will be held in a segregated escrow account until the minimum raise threshold is met. If the campaign does not reach its minimum close, all funds will be returned within 5 business days. Platform fees are non-refundable once the campaign closes successfully.',
    required: true,
  },
];

const CAMPAIGN = {
  id: 'CAMP-2026-0047',
  name: 'Meridian Green Infrastructure Fund III',
  issuer: 'Meridian Capital Partners',
  type: 'Private Equity — Infrastructure',
  targetRaise: 25_000_000,
  raisedToDate: 18_340_000,
  minTicket: 25_000,
  maxTicket: 500_000,
  closingDate: '2026-09-30',
  currency: 'USD',
  expectedReturn: '12–15% IRR',
  tenor: '7 years',
  riskRating: 'Medium',
  escrowAgent: 'First National Escrow Services Ltd',
  escrowAccount: 'FNESC-ESC-2026-0047',
  regulatoryRef: 'SEC Reg CF / Reg D 506(c)',
};

const INVESTOR = {
  id: 'INV-2026-00882',
  name: 'Alexandra Thornton',
  tier: 'sophisticated' as InvestorTier,
  kycStatus: 'verified',
  annualIncome: 420_000,
  netWorth: 2_800_000,
  investedThisYear: 87_500,
  portfolioValue: 1_240_000,
  accreditedExpiry: '2027-03-15',
};

const INITIAL_ELIGIBILITY_CHECKS: EligibilityCheck[] = [
  { id: 'kyc', label: 'KYC / AML Verification', description: 'Identity verified, no sanctions or PEP flags', status: 'pass', detail: 'Verified 2026-08-01 · Case KYC-2026-00441' },
  { id: 'accreditation', label: 'Investor Accreditation', description: 'Sophisticated investor status confirmed', status: 'pass', detail: 'Accredited until 2027-03-15 · Tier: Sophisticated' },
  { id: 'suitability', label: 'Suitability Assessment', description: 'Risk profile matches campaign risk rating', status: 'pass', detail: 'Risk tolerance: Medium-High · Campaign: Medium' },
  { id: 'jurisdiction', label: 'Jurisdiction Eligibility', description: 'Investor jurisdiction permitted for this offering', status: 'pass', detail: 'Residence: United States · Offering: Reg D 506(c)' },
  { id: 'compliance-hold', label: 'Compliance Hold Check', description: 'No active compliance holds on investor account', status: 'pass', detail: 'No holds detected · Last reviewed 2026-08-18' },
  { id: 'campaign-status', label: 'Campaign Status', description: 'Campaign is open and accepting commitments', status: 'pass', detail: 'Status: Open · Closing 2026-09-30' },
  { id: 'capacity', label: 'Campaign Capacity', description: 'Sufficient remaining capacity for commitment', status: 'warning', detail: 'Remaining: $6,660,000 · 26.6% of target unfilled' },
];

const LIMIT_RULES: LimitRule[] = [
  { id: 'annual-cap', label: 'Annual Investment Cap (Reg CF)', current: 87_500, limit: 107_000, unit: 'USD', status: 'warning' },
  { id: 'single-campaign', label: 'Single Campaign Limit', current: 0, limit: 500_000, unit: 'USD', status: 'ok' },
  { id: 'portfolio-concentration', label: 'Portfolio Concentration (per issuer)', current: 0, limit: 20, unit: '%', status: 'ok' },
  { id: 'illiquid-cap', label: 'Illiquid Asset Cap (of portfolio)', current: 31.2, limit: 40, unit: '%', status: 'ok' },
  { id: 'sector-exposure', label: 'Infrastructure Sector Exposure', current: 18.4, limit: 30, unit: '%', status: 'ok' },
];

const ALLOCATION_BUCKETS: AllocationBucket[] = [
  { id: 'pe', label: 'Private Equity', currentPct: 28.4, afterPct: 30.2, color: '#6366f1' },
  { id: 'infra', label: 'Infrastructure', currentPct: 18.4, afterPct: 20.6, color: '#0ea5e9' },
  { id: 'realestate', label: 'Real Estate', currentPct: 14.2, afterPct: 14.2, color: '#f59e0b' },
  { id: 'credit', label: 'Private Credit', currentPct: 22.1, afterPct: 22.1, color: '#10b981' },
  { id: 'liquid', label: 'Liquid / Public', currentPct: 16.9, afterPct: 12.9, color: '#8b5cf6' },
];

const ESCROW_CLAUSES = [
  { id: 'e1', title: '1. Escrow Establishment', body: 'Investor funds shall be held in a segregated escrow account at First National Escrow Services Ltd (FNESC), account reference FNESC-ESC-2026-0047, pending satisfaction of the minimum raise threshold of USD 10,000,000.' },
  { id: 'e2', title: '2. Release Conditions', body: 'Escrow funds shall be released to the Issuer upon: (a) achievement of the minimum raise threshold; (b) receipt of all required regulatory approvals; and (c) written confirmation from the Compliance Officer that all KYC/AML requirements have been satisfied for all committed investors.' },
  { id: 'e3', title: '3. Return of Funds', body: 'In the event the minimum raise threshold is not achieved by the Campaign Closing Date (2026-09-30), all escrowed funds shall be returned to investors within five (5) business days, without deduction, to the originating payment account.' },
  { id: 'e4', title: '4. Investor Representations', body: 'By proceeding, the Investor represents and warrants that: (a) the committed funds are from legitimate sources; (b) the investment is consistent with the Investor\'s financial situation and risk tolerance; (c) the Investor has read and understood the Offering Memorandum and all associated risk disclosures.' },
  { id: 'e5', title: '5. Fees & Charges', body: 'A platform fee of 1.5% of committed amount will be deducted from the escrow upon fund release. An annual management fee of 2.0% and a carried interest of 20% above an 8% hurdle rate apply as detailed in the Offering Memorandum.' },
  { id: 'e6', title: '6. Governing Law', body: 'This escrow arrangement and the subscription agreement are governed by the laws of the State of Delaware, United States. Any disputes shall be resolved by binding arbitration under JAMS rules.' },
];

const now = () => new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(n: number, unit = 'USD') {
  if (unit === 'USD') return '$' + n.toLocaleString('en-US');
  if (unit === '%') return n.toFixed(1) + '%';
  return n.toString();
}

function ProgressBar({ value, max, colorClass = 'bg-accent' }: { value: number; max: number; colorClass?: string }) {
  const pct = Math.min((value / max) * 100, 100);
  const isWarning = pct >= 80;
  return (
    <div className="w-full bg-surface-3 rounded-full h-1.5 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${isWarning ? 'bg-warning' : colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── Step 1: Eligibility ──────────────────────────────────────────────────────
function EligibilityStep({ onNext }: { onNext: () => void }) {
  const [checks] = useState<EligibilityCheck[]>(INITIAL_ELIGIBILITY_CHECKS);
  const failed = checks.filter(c => c.status === 'fail').length;
  const warnings = checks.filter(c => c.status === 'warning').length;
  const allPassed = failed === 0;

  const statusIcon = (s: EligibilityCheck['status']) => {
    if (s === 'pass') return <CheckCircle size={16} className="text-positive flex-shrink-0" />;
    if (s === 'fail') return <XCircle size={16} className="text-negative flex-shrink-0" />;
    if (s === 'warning') return <AlertTriangle size={16} className="text-warning flex-shrink-0" />;
    return <RefreshCw size={16} className="text-muted animate-spin flex-shrink-0" />;
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Eligibility Verification"
        subtitle="Automated checks confirm the investor meets all regulatory and platform requirements before committing."
        icon={Shield}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="KYC Status" value="Verified" trend="neutral" />
        <MetricCard label="Investor Tier" value="Sophisticated" trend="neutral" />
        <MetricCard label="Checks Passed" value={`${checks.filter(c => c.status === 'pass').length}/${checks.length}`} trend="up" />
        <MetricCard label="Warnings" value={warnings.toString()} trend={warnings > 0 ? 'down' : 'neutral'} />
      </div>

      <div className="card p-4 border border-border">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Users size={18} className="text-accent" />
          </div>
          <div>
            <p className="font-semibold text-foreground">{INVESTOR.name}</p>
            <p className="text-sm text-muted">{INVESTOR.id} · Tier: {INVESTOR.tier.charAt(0).toUpperCase() + INVESTOR.tier.slice(1)}</p>
          </div>
          <div className="ml-auto">
            <StatusBadge status="verified" label="KYC Verified" />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-border">
          <div>
            <p className="text-xs text-muted mb-0.5">Annual Income</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(INVESTOR.annualIncome)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Net Worth</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(INVESTOR.netWorth)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Invested This Year</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(INVESTOR.investedThisYear)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Accreditation Expiry</p>
            <p className="text-sm font-mono-data text-foreground">{INVESTOR.accreditedExpiry}</p>
          </div>
        </div>
      </div>

      <div className="card p-4 border border-border">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-info/10 flex items-center justify-center flex-shrink-0">
            <Building2 size={18} className="text-info" />
          </div>
          <div>
            <p className="font-semibold text-foreground">{CAMPAIGN.name}</p>
            <p className="text-sm text-muted">{CAMPAIGN.id} · {CAMPAIGN.type}</p>
          </div>
          <div className="ml-auto">
            <StatusBadge status="active" label="Open" />
          </div>
        </div>
        <div className="mb-3">
          <div className="flex justify-between text-xs text-muted mb-1">
            <span>Raise Progress</span>
            <span>{fmt(CAMPAIGN.raisedToDate)} / {fmt(CAMPAIGN.targetRaise)}</span>
          </div>
          <ProgressBar value={CAMPAIGN.raisedToDate} max={CAMPAIGN.targetRaise} colorClass="bg-positive" />
          <p className="text-xs text-muted mt-1">{((CAMPAIGN.raisedToDate / CAMPAIGN.targetRaise) * 100).toFixed(1)}% funded · Closes {CAMPAIGN.closingDate}</p>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
          <div>
            <p className="text-xs text-muted mb-0.5">Min Ticket</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(CAMPAIGN.minTicket)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Expected IRR</p>
            <p className="text-sm font-mono-data text-foreground">{CAMPAIGN.expectedReturn}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Risk Rating</p>
            <p className="text-sm font-mono-data text-foreground">{CAMPAIGN.riskRating}</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {checks.map(check => (
          <div
            key={check.id}
            className={`card p-3 border flex items-start gap-3 ${
              check.status === 'fail' ? 'border-negative/30 bg-negative/5' :
              check.status === 'warning'? 'border-warning/30 bg-warning/5' : 'border-border'
            }`}
          >
            {statusIcon(check.status)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{check.label}</p>
              <p className="text-xs text-muted">{check.description}</p>
              {check.detail && (
                <p className="text-xs text-muted/70 mt-0.5 font-mono-data">{check.detail}</p>
              )}
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
              check.status === 'pass' ? 'bg-positive/10 text-positive' :
              check.status === 'fail' ? 'bg-negative/10 text-negative' :
              check.status === 'warning'? 'bg-warning/10 text-warning' : 'bg-muted/10 text-muted'
            }`}>
              {check.status.toUpperCase()}
            </span>
          </div>
        ))}
      </div>

      {warnings > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
          <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-sm text-warning">
            {warnings} warning{warnings > 1 ? 's' : ''} detected. Review before proceeding. Commitment can still be submitted subject to compliance officer review.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!allPassed && failed > 0}
          className="btn-primary flex items-center gap-2"
        >
          Continue to Investor Limits <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Investor Limits ──────────────────────────────────────────────────
function InvestorLimitsStep({
  commitmentAmount,
  setCommitmentAmount,
  onNext,
  onBack,
}: {
  commitmentAmount: number;
  setCommitmentAmount: (v: number) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [inputVal, setInputVal] = useState(commitmentAmount.toString());
  const [touched, setTouched] = useState(false);

  const amount = parseFloat(inputVal.replace(/,/g, '')) || 0;
  const isValidAmount = amount >= CAMPAIGN.minTicket && amount <= CAMPAIGN.maxTicket;

  const annualCapRule = LIMIT_RULES.find(r => r.id === 'annual-cap')!;
  const projectedAnnual = annualCapRule.current + amount;
  const annualCapStatus: LimitRule['status'] = projectedAnnual > annualCapRule.limit ? 'breach' : projectedAnnual > annualCapRule.limit * 0.9 ? 'warning' : 'ok';

  const concentrationPct = amount > 0 ? (amount / INVESTOR.portfolioValue) * 100 : 0;
  const concentrationStatus: LimitRule['status'] = concentrationPct > 20 ? 'breach' : concentrationPct > 15 ? 'warning' : 'ok';

  const illiquidPct = 31.2 + (amount > 0 ? (amount / INVESTOR.portfolioValue) * 100 : 0);
  const illiquidStatus: LimitRule['status'] = illiquidPct > 40 ? 'breach' : illiquidPct > 35 ? 'warning' : 'ok';

  const dynamicRules: LimitRule[] = [
    { id: 'annual-cap', label: 'Annual Investment Cap (Reg CF)', current: projectedAnnual, limit: annualCapRule.limit, unit: 'USD', status: annualCapStatus },
    { id: 'single-campaign', label: 'Single Campaign Limit', current: amount, limit: 500_000, unit: 'USD', status: amount > 500_000 ? 'breach' : 'ok' },
    { id: 'portfolio-concentration', label: 'Portfolio Concentration (per issuer)', current: concentrationPct, limit: 20, unit: '%', status: concentrationStatus },
    { id: 'illiquid-cap', label: 'Illiquid Asset Cap (of portfolio)', current: illiquidPct, limit: 40, unit: '%', status: illiquidStatus },
    { id: 'sector-exposure', label: 'Infrastructure Sector Exposure', current: 18.4 + (amount > 0 ? (amount / INVESTOR.portfolioValue) * 100 : 0), limit: 30, unit: '%', status: 'ok' },
  ];

  const hasBreaches = dynamicRules.some(r => r.status === 'breach');

  const handleAmountChange = (v: string) => {
    setInputVal(v);
    setTouched(true);
    const parsed = parseFloat(v.replace(/,/g, '')) || 0;
    setCommitmentAmount(parsed);
  };

  const statusColor = (s: LimitRule['status']) => {
    if (s === 'breach') return 'text-negative';
    if (s === 'warning') return 'text-warning';
    return 'text-positive';
  };

  const barColor = (s: LimitRule['status']) => {
    if (s === 'breach') return 'bg-negative';
    if (s === 'warning') return 'bg-warning';
    return 'bg-positive';
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Investor Limits & Commitment Amount"
        subtitle="Set the commitment amount and verify it complies with regulatory caps and platform limits."
        icon={BarChart3}
      />

      <div className="card p-5 border border-border">
        <p className="text-sm font-semibold text-foreground mb-4">Commitment Amount</p>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1">
            <label className="text-xs text-muted mb-1 block">Amount (USD)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted font-mono-data">$</span>
              <input
                type="text"
                value={inputVal}
                onChange={e => handleAmountChange(e.target.value)}
                className="input pl-7 font-mono-data text-lg w-full"
                placeholder="50,000"
              />
            </div>
            {touched && !isValidAmount && amount > 0 && (
              <p className="text-xs text-negative mt-1">
                Amount must be between {fmt(CAMPAIGN.minTicket)} and {fmt(CAMPAIGN.maxTicket)}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-muted mb-1">Quick Select</p>
            <div className="flex gap-2">
              {[25_000, 50_000, 100_000].map(v => (
                <button
                  key={v}
                  onClick={() => { setInputVal(v.toString()); setCommitmentAmount(v); setTouched(true); }}
                  className={`btn-ghost text-xs px-2 py-1 ${amount === v ? 'bg-accent/10 text-accent border-accent/30' : ''}`}
                >
                  {fmt(v)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border text-center">
          <div>
            <p className="text-xs text-muted mb-0.5">Min Ticket</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(CAMPAIGN.minTicket)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Max Ticket</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(CAMPAIGN.maxTicket)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Remaining Capacity</p>
            <p className="text-sm font-mono-data text-foreground">{fmt(CAMPAIGN.targetRaise - CAMPAIGN.raisedToDate)}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground">Regulatory & Platform Limits</p>
        {dynamicRules.map(rule => {
          const pct = (rule.current / rule.limit) * 100;
          return (
            <div key={rule.id} className={`card p-3 border ${rule.status === 'breach' ? 'border-negative/30 bg-negative/5' : rule.status === 'warning' ? 'border-warning/30 bg-warning/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-foreground">{rule.label}</p>
                <span className={`text-xs font-mono-data font-semibold ${statusColor(rule.status)}`}>
                  {rule.unit === 'USD' ? fmt(rule.current) : rule.current.toFixed(1) + '%'} / {rule.unit === 'USD' ? fmt(rule.limit) : rule.limit + '%'}
                </span>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor(rule.status)}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              {rule.status === 'breach' && (
                <p className="text-xs text-negative mt-1 flex items-center gap-1">
                  <AlertCircle size={11} /> Limit exceeded — commitment amount must be reduced
                </p>
              )}
              {rule.status === 'warning' && (
                <p className="text-xs text-warning mt-1 flex items-center gap-1">
                  <AlertTriangle size={11} /> Approaching limit — review before proceeding
                </p>
              )}
            </div>
          );
        })}
      </div>

      {hasBreaches && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-negative/10 border border-negative/20">
          <XCircle size={16} className="text-negative flex-shrink-0 mt-0.5" />
          <p className="text-sm text-negative">One or more limit breaches detected. Reduce the commitment amount to proceed.</p>
        </div>
      )}

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-ghost flex items-center gap-2">
          <ChevronLeft size={16} /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!isValidAmount || hasBreaches || amount === 0}
          className="btn-primary flex items-center gap-2"
        >
          Continue to Funding Method <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Funding Method ───────────────────────────────────────────────────
function FundingMethodStep({
  commitmentAmount,
  selectedMethod,
  setSelectedMethod,
  onNext,
  onBack,
}: {
  commitmentAmount: number;
  selectedMethod: FundingMethodId | null;
  setSelectedMethod: (id: FundingMethodId) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [disclosuresAcknowledged, setDisclosuresAcknowledged] = useState<Set<string>>(new Set());
  const [expandedDisclosure, setExpandedDisclosure] = useState<string | null>('d1');

  const method = FUNDING_METHODS.find(m => m.id === selectedMethod);
  const swiftFee = method ? Math.max(method.feeAmount * commitmentAmount, 50) : 0;
  const totalFee = method?.id === 'swift' ? swiftFee : 0;
  const netAmount = commitmentAmount - totalFee;

  const allDisclosuresAcknowledged = DISCLOSURES.every(d => disclosuresAcknowledged.has(d.id));

  const toggleDisclosure = (id: string) => {
    setExpandedDisclosure(prev => prev === id ? null : id);
    setDisclosuresAcknowledged(prev => new Set([...prev, id]));
  };

  const isMethodEligible = (m: FundingMethod) => {
    return commitmentAmount >= m.minAmount && commitmentAmount <= m.maxAmount;
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Funding Method & Pre-Commitment Disclosures"
        subtitle="Select how you will transfer funds to escrow and acknowledge all required disclosures before proceeding."
        icon={CreditCard}
      />

      {/* Commitment summary */}
      <div className="card p-4 border border-border bg-surface-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted mb-0.5">Commitment Amount</p>
            <p className="text-2xl font-bold font-mono-data text-foreground">{fmt(commitmentAmount)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted mb-0.5">Campaign</p>
            <p className="text-sm text-foreground font-medium">{CAMPAIGN.name}</p>
            <p className="text-xs text-muted font-mono-data">{CAMPAIGN.id}</p>
          </div>
        </div>
      </div>

      {/* Funding method selection */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-3">Select Funding Method</p>
        <div className="space-y-3">
          {FUNDING_METHODS.map(m => {
            const eligible = isMethodEligible(m);
            const isSelected = selectedMethod === m.id;
            const MethodIcon = m.icon;

            return (
              <button
                key={m.id}
                onClick={() => eligible && setSelectedMethod(m.id)}
                disabled={!eligible}
                className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
                  !eligible
                    ? 'opacity-40 cursor-not-allowed border-border bg-surface-2'
                    : isSelected
                    ? 'border-accent bg-accent/5 ring-1 ring-accent/30' :'border-border bg-surface-2 hover:border-border-strong hover:bg-surface-3'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Radio indicator */}
                  <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                    isSelected ? 'border-accent bg-accent' : 'border-border'
                  }`}>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>

                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-accent/15' : 'bg-surface-3'
                  }`}>
                    <MethodIcon size={17} className={isSelected ? 'text-accent' : 'text-muted'} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-sm font-semibold ${isSelected ? 'text-foreground' : 'text-foreground'}`}>{m.label}</span>
                      {m.badge && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${m.badgeColor}`}>{m.badge}</span>
                      )}
                      {!eligible && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-surface-4 text-muted border border-border">
                          {commitmentAmount < m.minAmount ? `Min ${fmt(m.minAmount)}` : `Max ${fmt(m.maxAmount)}`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted mb-2">{m.description}</p>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1 text-muted">
                        <Clock size={11} /> {m.processingTime}
                      </span>
                      <span className={`flex items-center gap-1 ${m.feeAmount > 0 ? 'text-warning' : 'text-positive'}`}>
                        <Banknote size={11} /> {m.fee}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded details when selected */}
                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-accent/20">
                    <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wider">Transfer Instructions</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {m.details.map(d => (
                        <div key={d.label} className="bg-surface-3 rounded-lg px-3 py-2">
                          <p className="text-xs text-muted mb-0.5">{d.label}</p>
                          <p className="text-xs font-mono-data text-foreground font-medium">{d.value}</p>
                        </div>
                      ))}
                    </div>
                    {m.id === 'swift' && (
                      <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-warning/10 border border-warning/20">
                        <AlertTriangle size={13} className="text-warning flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-warning">
                          SWIFT fee: {fmt(Math.round(swiftFee))} will be deducted. Net to escrow: {fmt(Math.round(netAmount))}.
                        </p>
                      </div>
                    )}
                    {m.id === 'crypto' && (
                      <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-info/10 border border-info/20">
                        <Info size={13} className="text-info flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-info">
                          A source-of-funds declaration for digital assets will be required before escrow confirmation.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Pre-commitment disclosures */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-foreground">Pre-Commitment Disclosures</p>
          <span className="text-xs text-muted">{disclosuresAcknowledged.size}/{DISCLOSURES.length} acknowledged</span>
        </div>
        <div className="space-y-2">
          {DISCLOSURES.map(disc => (
            <div
              key={disc.id}
              className={`card border overflow-hidden transition-colors ${disclosuresAcknowledged.has(disc.id) ? 'border-positive/30 bg-positive/3' : 'border-border'}`}
            >
              <button
                onClick={() => toggleDisclosure(disc.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-3 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {disclosuresAcknowledged.has(disc.id)
                    ? <CheckCircle size={14} className="text-positive flex-shrink-0" />
                    : <FileCheck size={14} className="text-muted flex-shrink-0" />
                  }
                  <span className="text-sm font-medium text-foreground">{disc.title}</span>
                  {disc.required && (
                    <span className="text-xs text-negative font-medium">Required</span>
                  )}
                </div>
                <ChevronRight size={14} className={`text-muted transition-transform flex-shrink-0 ${expandedDisclosure === disc.id ? 'rotate-90' : ''}`} />
              </button>
              {expandedDisclosure === disc.id && (
                <div className="px-4 pb-4 pt-1 border-t border-border">
                  <p className="text-sm text-muted leading-relaxed">{disc.body}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {!allDisclosuresAcknowledged && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-info/10 border border-info/20 mt-3">
            <Info size={16} className="text-info flex-shrink-0 mt-0.5" />
            <p className="text-sm text-info">Please expand and review all {DISCLOSURES.length} required disclosures before proceeding.</p>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-ghost flex items-center gap-2">
          <ChevronLeft size={16} /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!selectedMethod || !allDisclosuresAcknowledged}
          className="btn-primary flex items-center gap-2"
        >
          Continue to Portfolio Allocation <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Portfolio Allocation ─────────────────────────────────────────────
function PortfolioAllocationStep({
  commitmentAmount,
  onNext,
  onBack,
}: {
  commitmentAmount: number;
  onNext: () => void;
  onBack: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  const totalAfter = ALLOCATION_BUCKETS.reduce((s, b) => s + b.afterPct, 0);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Portfolio Allocation Impact"
        subtitle="Review how this commitment changes your portfolio allocation across asset classes."
        icon={PieChart}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Commitment" value={`$${(commitmentAmount / 1000).toFixed(0)}K`} trend="neutral" />
        <MetricCard label="Portfolio Value" value={`$${(INVESTOR.portfolioValue / 1000).toFixed(0)}K`} trend="neutral" />
        <MetricCard label="New Allocation %" value={`${((commitmentAmount / INVESTOR.portfolioValue) * 100).toFixed(1)}%`} trend="neutral" />
        <MetricCard label="Asset Class" value="Infrastructure" trend="neutral" />
      </div>

      <div className="card border border-border overflow-hidden">
        <div className="grid grid-cols-4 gap-3 px-4 py-2 bg-surface-3 border-b border-border text-xs text-muted font-medium">
          <span>Asset Class</span>
          <span className="text-right">Current</span>
          <span className="text-right">After</span>
          <span className="text-right">Change</span>
        </div>
        {ALLOCATION_BUCKETS.map(bucket => {
          const delta = bucket.afterPct - bucket.currentPct;
          const isChanged = Math.abs(delta) > 0.01;
          return (
            <div key={bucket.id} className={`grid grid-cols-4 gap-3 px-4 py-3 border-b border-border last:border-0 items-center ${isChanged ? 'bg-accent/3' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.color }} />
                <span className="text-sm text-foreground">{bucket.label}</span>
                {isChanged && <span className="text-xs bg-accent/10 text-accent px-1.5 py-0.5 rounded-full">Updated</span>}
              </div>
              <div className="text-right">
                <span className="text-sm font-mono-data text-muted">{bucket.currentPct.toFixed(1)}%</span>
                <div className="w-full bg-surface-3 rounded-full h-1 mt-1 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${bucket.currentPct}%`, backgroundColor: bucket.color, opacity: 0.5 }} />
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm font-mono-data text-foreground font-semibold">{bucket.afterPct.toFixed(1)}%</span>
                <div className="w-full bg-surface-3 rounded-full h-1 mt-1 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${bucket.afterPct}%`, backgroundColor: bucket.color }} />
                </div>
              </div>
              <div className="text-right">
                {isChanged ? (
                  <span className={`text-sm font-mono-data font-semibold ${delta > 0 ? 'text-positive' : 'text-negative'}`}>
                    {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
                  </span>
                ) : (
                  <span className="text-sm font-mono-data text-muted">—</span>
                )}
              </div>
            </div>
          );
        })}
        <div className="grid grid-cols-4 gap-3 px-4 py-2 bg-surface-3 border-t border-border text-xs font-semibold text-foreground">
          <span>Total</span>
          <span className="text-right font-mono-data">100.0%</span>
          <span className="text-right font-mono-data">{totalAfter.toFixed(1)}%</span>
          <span className="text-right font-mono-data">—</span>
        </div>
      </div>

      <div className="card p-4 border border-border">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-info flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground mb-1">Diversification Analysis</p>
            <p className="text-sm text-muted">
              This commitment increases your Infrastructure exposure from 18.4% to 20.6%, within the 30% sector cap.
              Liquid assets decrease from 16.9% to 12.9% — ensure you maintain adequate liquidity for near-term obligations.
              Overall portfolio diversification remains within recommended parameters.
            </p>
          </div>
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={e => setAcknowledged(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-accent"
        />
        <span className="text-sm text-muted">
          I have reviewed the portfolio allocation impact and confirm this commitment aligns with my investment objectives and risk tolerance.
        </span>
      </label>

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-ghost flex items-center gap-2">
          <ChevronLeft size={16} /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!acknowledged}
          className="btn-primary flex items-center gap-2"
        >
          Continue to Escrow Agreement <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 5: Escrow Agreement ─────────────────────────────────────────────────
function EscrowAgreementStep({
  commitmentAmount,
  onNext,
  onBack,
}: {
  commitmentAmount: number;
  onNext: () => void;
  onBack: () => void;
}) {
  const [readClauses, setReadClauses] = useState<Set<string>>(new Set());
  const [agreed, setAgreed] = useState(false);
  const [expandedClause, setExpandedClause] = useState<string | null>('e1');

  const platformFee = commitmentAmount * 0.015;
  const netToEscrow = commitmentAmount - platformFee;

  const allRead = ESCROW_CLAUSES.every(c => readClauses.has(c.id));

  const toggleClause = (id: string) => {
    setExpandedClause(prev => prev === id ? null : id);
    setReadClauses(prev => new Set([...prev, id]));
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Escrow Agreement Review"
        subtitle="Read and acknowledge the escrow terms before submitting your subscription."
        icon={Landmark}
      />

      <div className="card p-4 border border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
            <Landmark size={18} className="text-accent" />
          </div>
          <div>
            <p className="font-semibold text-foreground">{CAMPAIGN.escrowAgent}</p>
            <p className="text-xs text-muted font-mono-data">Account: {CAMPAIGN.escrowAccount}</p>
          </div>
          <div className="ml-auto">
            <StatusBadge status="active" label="Active Escrow" />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-border">
          <div>
            <p className="text-xs text-muted mb-0.5">Commitment Amount</p>
            <p className="text-sm font-mono-data text-foreground font-semibold">{fmt(commitmentAmount)}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Platform Fee (1.5%)</p>
            <p className="text-sm font-mono-data text-negative">−{fmt(Math.round(platformFee))}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Net to Escrow</p>
            <p className="text-sm font-mono-data text-positive font-semibold">{fmt(Math.round(netToEscrow))}</p>
          </div>
          <div>
            <p className="text-xs text-muted mb-0.5">Regulatory Ref</p>
            <p className="text-sm font-mono-data text-foreground">{CAMPAIGN.regulatoryRef}</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold text-foreground">Agreement Clauses</p>
          <span className="text-xs text-muted">{readClauses.size}/{ESCROW_CLAUSES.length} reviewed</span>
        </div>
        {ESCROW_CLAUSES.map(clause => (
          <div
            key={clause.id}
            className={`card border overflow-hidden transition-colors ${readClauses.has(clause.id) ? 'border-positive/30' : 'border-border'}`}
          >
            <button
              onClick={() => toggleClause(clause.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-3 transition-colors"
            >
              <div className="flex items-center gap-2">
                {readClauses.has(clause.id)
                  ? <CheckCircle size={14} className="text-positive flex-shrink-0" />
                  : <Eye size={14} className="text-muted flex-shrink-0" />
                }
                <span className="text-sm font-medium text-foreground">{clause.title}</span>
              </div>
              <ChevronRight size={14} className={`text-muted transition-transform ${expandedClause === clause.id ? 'rotate-90' : ''}`} />
            </button>
            {expandedClause === clause.id && (
              <div className="px-4 pb-4 pt-1 border-t border-border">
                <p className="text-sm text-muted leading-relaxed">{clause.body}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {!allRead && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-info/10 border border-info/20">
          <Info size={16} className="text-info flex-shrink-0 mt-0.5" />
          <p className="text-sm text-info">Please expand and review all {ESCROW_CLAUSES.length} clauses before agreeing.</p>
        </div>
      )}

      <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${agreed ? 'border-positive/30 bg-positive/5' : 'border-border'}`}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
          disabled={!allRead}
          className="mt-0.5 w-4 h-4 accent-accent"
        />
        <span className={`text-sm ${!allRead ? 'text-muted/50' : 'text-muted'}`}>
          I have read and agree to all escrow agreement terms, the subscription agreement, and the offering memorandum for {CAMPAIGN.name}. I understand the risks involved and confirm this investment is suitable for my financial situation.
        </span>
      </label>

      <div className="flex items-center gap-2 text-xs text-muted">
        <Lock size={12} />
        <span>Agreement will be cryptographically signed and stored in the document vault upon submission.</span>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-ghost flex items-center gap-2">
          <ChevronLeft size={16} /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!agreed}
          className="btn-primary flex items-center gap-2"
        >
          Review & Confirm Subscription <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 6: Confirmation ─────────────────────────────────────────────────────
function ConfirmationStep({
  commitmentAmount,
  selectedMethod,
  onBack,
  onSubmit,
  submitted,
  txRef,
}: {
  commitmentAmount: number;
  selectedMethod: FundingMethodId | null;
  onBack: () => void;
  onSubmit: () => void;
  submitted: boolean;
  txRef: string;
}) {
  const platformFee = commitmentAmount * 0.015;
  const netToEscrow = commitmentAmount - platformFee;
  const method = FUNDING_METHODS.find(m => m.id === selectedMethod);

  if (submitted) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-positive/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-positive" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Subscription Confirmed</h2>
          <p className="text-muted text-sm max-w-md mx-auto">
            Your investment commitment has been submitted and a transaction has been created. Funds will be held in escrow pending campaign close.
          </p>
        </div>

        <div className="card p-5 border border-positive/30 bg-positive/5">
          <p className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Hash size={14} className="text-positive" /> Transaction Reference
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted mb-0.5">Transaction ID</p>
              <p className="text-sm font-mono-data text-foreground font-semibold">{txRef}</p>
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Status</p>
              <StatusBadge status="pending" label="Pending Escrow" />
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Campaign</p>
              <p className="text-sm text-foreground">{CAMPAIGN.name}</p>
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Committed Amount</p>
              <p className="text-sm font-mono-data text-foreground font-semibold">{fmt(commitmentAmount)}</p>
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Net to Escrow</p>
              <p className="text-sm font-mono-data text-positive">{fmt(Math.round(netToEscrow))}</p>
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Funding Method</p>
              <p className="text-sm text-foreground">{method?.label ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Escrow Account</p>
              <p className="text-sm font-mono-data text-foreground">{CAMPAIGN.escrowAccount}</p>
            </div>
            <div>
              <p className="text-xs text-muted mb-0.5">Investor</p>
              <p className="text-sm text-foreground">{INVESTOR.name}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 border border-border">
          <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Clock size={14} className="text-accent" /> Next Steps
          </p>
          <div className="space-y-2">
            {[
              { label: 'Subscription agreement signed and stored in Document Vault', done: true },
              { label: `Fund transfer instructions sent via ${method?.label ?? 'selected method'}`, done: true },
              { label: 'Compliance review of commitment (1–2 business days)', done: false },
              { label: 'Escrow confirmation upon fund receipt', done: false },
              { label: 'Campaign close and fund release to issuer (est. 2026-09-30)', done: false },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                {step.done
                  ? <CheckCircle size={14} className="text-positive flex-shrink-0" />
                  : <div className="w-3.5 h-3.5 rounded-full border-2 border-muted/40 flex-shrink-0" />
                }
                <span className={`text-sm ${step.done ? 'text-foreground' : 'text-muted'}`}>{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 justify-center">
          <button className="btn-ghost flex items-center gap-2">
            <Download size={14} /> Download Confirmation
          </button>
          <button className="btn-ghost flex items-center gap-2">
            <Eye size={14} /> View in Portfolio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Subscription Confirmation"
        subtitle="Review your commitment details one final time before submitting."
        icon={CheckCircle}
      />

      <div className="card p-5 border border-border">
        <p className="text-sm font-semibold text-foreground mb-4">Commitment Summary</p>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Campaign</span>
            <span className="text-sm text-foreground font-medium">{CAMPAIGN.name}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Issuer</span>
            <span className="text-sm text-foreground">{CAMPAIGN.issuer}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Investor</span>
            <span className="text-sm text-foreground">{INVESTOR.name} · {INVESTOR.id}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Commitment Amount</span>
            <span className="text-lg font-mono-data font-bold text-foreground">{fmt(commitmentAmount)}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Funding Method</span>
            <span className="text-sm text-foreground flex items-center gap-1.5">
              {method && <method.icon size={13} className="text-muted" />}
              {method?.label ?? '—'}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Platform Fee (1.5%)</span>
            <span className="text-sm font-mono-data text-negative">−{fmt(Math.round(platformFee))}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted font-semibold">Net to Escrow</span>
            <span className="text-sm font-mono-data font-bold text-positive">{fmt(Math.round(netToEscrow))}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Escrow Agent</span>
            <span className="text-sm text-foreground">{CAMPAIGN.escrowAgent}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm text-muted">Expected IRR</span>
            <span className="text-sm font-mono-data text-foreground">{CAMPAIGN.expectedReturn}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-muted">Investment Tenor</span>
            <span className="text-sm font-mono-data text-foreground">{CAMPAIGN.tenor}</span>
          </div>
        </div>
      </div>

      <div className="card p-4 border border-accent/30 bg-accent/5">
        <div className="flex items-start gap-3">
          <Zap size={16} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground mb-1">Transaction Creation</p>
            <p className="text-sm text-muted">
              Submitting this form will create a double-entry ledger transaction, generate a subscription agreement,
              initiate escrow hold, and emit an investment commitment event to the compliance and finance systems.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-ghost flex items-center gap-2">
          <ChevronLeft size={16} /> Back
        </button>
        <button
          onClick={onSubmit}
          className="btn-primary flex items-center gap-2 bg-positive hover:bg-positive/90"
        >
          <BadgeCheck size={16} /> Submit Subscription
        </button>
      </div>
    </div>
  );
}

// ─── Main Flow ────────────────────────────────────────────────────────────────
export default function InvestmentCommitmentFlow() {
  const [currentStep, setCurrentStep] = useState<StepId>('eligibility');
  const [commitmentAmount, setCommitmentAmount] = useState(50_000);
  const [selectedMethod, setSelectedMethod] = useState<FundingMethodId | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [txRef] = useState('TXN-2026-88421');

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);

  const goNext = () => {
    const next = STEPS[stepIndex + 1];
    if (next) setCurrentStep(next.id);
  };
  const goBack = () => {
    const prev = STEPS[stepIndex - 1];
    if (prev) setCurrentStep(prev.id);
  };
  const handleSubmit = () => setSubmitted(true);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="border-b border-border bg-surface-2 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
            <TrendingUp size={18} className="text-accent" />
          </div>
          <div>
            <h1 className="text-base font-bold text-foreground">Investment Commitment</h1>
            <p className="text-xs text-muted">{CAMPAIGN.name} · {CAMPAIGN.id}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Lock size={13} className="text-muted" />
            <span className="text-xs text-muted">Secure Commitment Flow</span>
          </div>
        </div>
      </div>

      {/* Step progress */}
      <div className="border-b border-border bg-surface-2 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-1 overflow-x-auto">
          {STEPS.map((step, idx) => {
            const isActive = step.id === currentStep;
            const isDone = idx < stepIndex || (submitted && step.id === 'confirmation');
            const StepIcon = step.icon;
            return (
              <React.Fragment key={step.id}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-shrink-0 transition-colors ${
                  isActive ? 'bg-accent/10 text-accent' : isDone ? 'text-positive' : 'text-muted'
                }`}>
                  {isDone
                    ? <CheckCircle size={14} className="flex-shrink-0" />
                    : <StepIcon size={14} className="flex-shrink-0" />
                  }
                  <span className={`text-xs font-medium ${isActive ? 'text-accent' : isDone ? 'text-positive' : 'text-muted'}`}>
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ChevronRight size={12} className="text-muted/40 flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          {currentStep === 'eligibility' && (
            <EligibilityStep onNext={goNext} />
          )}
          {currentStep === 'limits' && (
            <InvestorLimitsStep
              commitmentAmount={commitmentAmount}
              setCommitmentAmount={setCommitmentAmount}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {currentStep === 'funding' && (
            <FundingMethodStep
              commitmentAmount={commitmentAmount}
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {currentStep === 'allocation' && (
            <PortfolioAllocationStep
              commitmentAmount={commitmentAmount}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {currentStep === 'escrow' && (
            <EscrowAgreementStep
              commitmentAmount={commitmentAmount}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {currentStep === 'confirmation' && (
            <ConfirmationStep
              commitmentAmount={commitmentAmount}
              selectedMethod={selectedMethod}
              onBack={goBack}
              onSubmit={handleSubmit}
              submitted={submitted}
              txRef={txRef}
            />
          )}
        </div>
      </div>
    </div>
  );
}
